import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel,
} from '../ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BulkDeleteBar = ({ selected, onClear, onDone }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const users = Object.values(selected);
  const paidCount = users.filter((u) => u.paid).length;
  if (users.length === 0) return null;

  const run = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/users/bulk-delete`, { user_ids: users.map((u) => u.user_id) }, { withCredentials: true });
      const { scheduled, skipped, message } = res.data;
      toast.success(message);
      skipped.forEach((s) => toast.warning(`Skipped ${s.email || s.user_id}: ${s.reason}`));
      setOpen(false);
      onClear();
      onDone(scheduled.length);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="sticky bottom-4 z-30 mt-4 mx-auto w-fit flex items-center gap-3 bg-slate-900 text-white rounded-full pl-5 pr-2 py-2 shadow-2xl animate-in fade-in slide-in-from-bottom-2"
        data-testid="bulk-delete-bar">
        <span className="text-sm font-medium" data-testid="bulk-selected-count">{users.length} selected</span>
        <Button size="sm" variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800 rounded-full" onClick={onClear}
          data-testid="bulk-clear-btn"><X className="w-4 h-4 mr-1" />Clear</Button>
        <Button size="sm" className="bg-red-600 hover:bg-red-500 rounded-full" onClick={() => setOpen(true)} data-testid="bulk-delete-btn">
          <Trash2 className="w-4 h-4 mr-1.5" />Schedule {users.length} for deletion
        </Button>
      </div>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Schedule {users.length} users for deletion?</AlertDialogTitle>
            <AlertDialogDescription>
              They're locked out now, renewals stop, and they're permanently deleted in 14 days unless restored.
              Admins and your own account are skipped.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {paidCount > 0 && (
            <p className="text-sm rounded-md bg-red-50 text-red-700 border border-red-200 px-3 py-2" data-testid="bulk-delete-paid-warning">
              {paidCount} selected {paidCount === 1 ? 'user still has' : 'users still have'} a paid subscription. Renewal will be turned off.
            </p>
          )}
          <ul className="max-h-48 overflow-y-auto text-sm border rounded-md divide-y" data-testid="bulk-delete-email-list">
            {users.map((u) => (
              <li key={u.user_id} className="px-3 py-1.5 flex justify-between">
                {u.email}{u.paid && <span className="text-xs font-medium text-red-600">Paid subscriber</span>}
              </li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-delete-cancel">Cancel</AlertDialogCancel>
            <Button variant="destructive" disabled={busy} onClick={run} data-testid="bulk-delete-confirm">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Schedule ${users.length} for deletion`}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default BulkDeleteBar;
